import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlayCircle, 
  Clock, 
  BookOpen,
  Search,
  SlidersHorizontal,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const MyCourses = () => {
  const [activeTab, setActiveTab] = useState('in-progress');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchEnrolledCourses = async () => {
      try {
        setLoading(true);
        
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error('No authenticated user found');
        }

        // Fetch enrollments with course details and lessons
        const { data: enrollments, error: enrollmentsError } = await supabase
          .from('enrollments')
          .select(`
            *,
            course:courses(
              id,
              title,
              description,
              thumbnail_url,
              category_id,
              lessons(id, title, duration)
            ),
            lesson_progress(
              lesson_id,
              completed,
              completed_at
            )
          `)
          .eq('user_id', user.id);

        if (enrollmentsError) throw enrollmentsError;

        const formattedCourses = enrollments.map(enrollment => {
          const lessons = enrollment.course.lessons || [];
          const lessonsProgress = enrollment.lesson_progress || [];
          const completedLessons = lessonsProgress.filter(lp => lp.completed).length;
          const progress = lessons.length > 0 ? (completedLessons / lessons.length) * 100 : 0;

          // Calculate total course duration
          const totalDuration = lessons.reduce((acc, lesson) => {
            if (!lesson.duration) return acc;
            const [hours, minutes] = lesson.duration.split(':').map(Number);
            return acc + (hours * 60) + minutes;
          }, 0);

          return {
            id: enrollment.course.id,
            title: enrollment.course.title,
            category: enrollment.course.category_id,
            completedLessons,
            totalLessons: lessons.length,
            lastAccessed: formatLastAccessed(enrollment.last_accessed),
            progress: Math.round(progress),
            thumbnail: enrollment.course.thumbnail_url || "/api/placeholder/400/225",
            status: enrollment.status,
            totalDuration,
            nextLesson: findNextLesson(lessons, lessonsProgress)
          };
        });

        setCourses(formattedCourses);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching courses:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEnrolledCourses();
  }, []);

  const findNextLesson = (lessons, progress) => {
    const sortedLessons = [...lessons].sort((a, b) => a.order_number - b.order_number);
    return sortedLessons.find(lesson => 
      !progress.find(p => p.lesson_id === lesson.id && p.completed)
    );
  };

  const formatLastAccessed = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const lastAccess = new Date(date);
    const diffDays = Math.floor((now - lastAccess) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0 
      ? `${hours}h ${remainingMinutes}m`
      : `${remainingMinutes}m`;
  };

  const CourseCard = ({ course }) => {
    const navigate = useNavigate();

    const handleCardClick = (e) => {
      // Prevent navigation if click was on the continue/review button
      if (!e.target.closest('button')) {
        navigate(`/courses/${course.id}/watch`);
      }
    };

    return (
      <div 
        className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden cursor-pointer"
        onClick={handleCardClick}
      >
      <div className="relative">
        <img 
          src={course.thumbnail} 
          alt={course.title}
          className="w-full h-48 object-cover"
        />
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
          <span className="text-sm font-medium text-blue-900">{course.category}</span>
        </div>
        {course.progress === 100 && (
          <div className="absolute bottom-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full flex items-center gap-1.5">
            <CheckCircle size={14} />
            <span className="text-sm font-medium">Completed</span>
          </div>
        )}
      </div>
      
      <div className="p-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">{course.title}</h3>
        
        <div className="space-y-4">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span className="flex items-center gap-1.5">
              <BookOpen size={16} />
              {course.completedLessons}/{course.totalLessons} Lessons
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={16} />
              {formatDuration(course.totalDuration)}
            </span>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                {course.progress}% Complete
              </span>
              <span className="text-sm text-gray-500">
                Last accessed: {course.lastAccessed}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  course.progress === 100 ? 'bg-green-500' : 'bg-blue-900'
                }`}
                style={{ width: `${course.progress}%` }}
              />
            </div>
          </div>

          {course.nextLesson && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Next Lesson:</span> {course.nextLesson.title}
            </div>
          )}
        </div>
        
        <button 
          className={`mt-4 w-full py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 ${
            course.progress === 100
              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              : 'bg-blue-900 text-white hover:bg-blue-800'
          }`}
          onClick={() => handleContinueCourse(course.id, course.nextLesson?.id)}
        >
          <PlayCircle size={18} />
          {course.progress === 100 ? 'Review Course' : 'Continue Learning'}
        </button>
      </div>
    </div>
    );
  };

  const handleContinueCourse = async (courseId, lessonId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Update last accessed timestamp
      const { error } = await supabase
        .from('enrollments')
        .update({ last_accessed: new Date().toISOString() })
        .match({ course_id: courseId, user_id: user.id });

      if (error) throw error;

      // TODO: Navigate to specific lesson or course overview
      // history.push(`/course/${courseId}/lesson/${lessonId || ''}`);
    } catch (err) {
      console.error('Error updating course access:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'completed' ? course.progress === 100 : course.progress < 100;
    return matchesSearch && matchesTab;
  });

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Courses</h1>
          <p className="text-gray-600 mt-1">Track and manage your learning progress</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search your courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full md:w-64 pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-900/20"
            />
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          </div>
          
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
            <SlidersHorizontal size={18} className="text-gray-600" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-gray-200 mb-8">
        <button
          onClick={() => setActiveTab('in-progress')}
          className={`pb-4 text-sm font-medium transition-colors relative ${
            activeTab === 'in-progress'
              ? 'text-blue-900'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          In Progress
          {activeTab === 'in-progress' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-900" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`pb-4 text-sm font-medium transition-colors relative ${
            activeTab === 'completed'
              ? 'text-blue-900'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Completed
          {activeTab === 'completed' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-900" />
          )}
        </button>
      </div>

      {/* Course Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCourses.map(course => (
          <CourseCard key={course.id} course={course} />
        ))}
      </div>

      {/* Empty State */}
      {filteredCourses.length === 0 && (
        <div className="text-center py-12">
          <div className="bg-gray-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <BookOpen size={24} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No {activeTab === 'completed' ? 'completed' : 'in-progress'} courses
          </h3>
          <p className="text-gray-600">
            {activeTab === 'completed' 
              ? 'Complete your courses to see them here'
              : 'Start learning to see your courses here'}
          </p>
        </div>
      )}
    </div>
  );
};

export default MyCourses;
